import { Details } from "../components";

export function Legal() {
  return (
    <>
      <h1>Legal</h1>

      <h2>Corporate Structure</h2>
      <p>
        <strong>Taiga AI Oy</strong> — Finnish limited liability company
        (Oy). Incorporated March 11, 2026. Helsinki, Finland.
      </p>

      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Legal entity</td>
            <td>Taiga AI Oy</td>
          </tr>
          <tr>
            <td>Business ID (Y-tunnus)</td>
            <td>3607851-2</td>
          </tr>
          <tr>
            <td>Jurisdiction</td>
            <td>Finland</td>
          </tr>
          <tr>
            <td>Company form</td>
            <td>Osakeyhtiö (Oy) — Finnish limited liability company</td>
          </tr>
          <tr>
            <td>Incorporated</td>
            <td>March 11, 2026</td>
          </tr>
          <tr>
            <td>Registered office</td>
            <td>c/o Lapinlahdenkatu 16, 00180 Helsinki, Finland</td>
          </tr>
          <tr>
            <td>TOL classification</td>
            <td>58290 — Other software publishing</td>
          </tr>
          <tr>
            <td>Financial year</td>
            <td>Calendar year</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>Ownership</h2>
      <p>
        Four founders with equal equity split. Standard 4-year vesting with
        1-year cliff, monthly thereafter. No employee option pool at this
        stage — expected to be established as part of pre-seed round
        negotiation.
      </p>

      <table>
        <thead>
          <tr>
            <th>Founder</th>
            <th>Role</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Mikko Laakkonen</td>
            <td>CEO</td>
          </tr>
          <tr>
            <td>Teemu Ijas</td>
            <td>CTO</td>
          </tr>
          <tr>
            <td>Henri Gronlund</td>
            <td>Chair &amp; CCO</td>
          </tr>
          <tr>
            <td>Anton Floor</td>
            <td>CAIO</td>
          </tr>
        </tbody>
      </table>

      <p>All founders hold shares through their personal holding companies.</p>

      <p>
        The equal split reflects equal commitment, complementary skills,
        shared history (10+ years across multiple companies), and full-time
        dedication from all four founders. Each founder controls a distinct
        domain with minimal overlap. The shareholders' agreement includes
        deadlock resolution mechanisms to prevent governance paralysis.
        This structure was a deliberate choice — not a default.
      </p>

      <hr />

      <h2>Founders' Agreement</h2>
      <p>
        Being finalized with Hannes Snellman Attorneys. Expected completion
        within weeks. The agreement covers:
      </p>

      <ul>
        <li>Equal equity split across all four founders</li>
        <li>Role definitions and areas of responsibility</li>
        <li>IP assignment to the company at incorporation</li>
        <li>Decision-making framework</li>
        <li>Non-compete provisions</li>
        <li>Departure and good/bad leaver provisions</li>
        <li>Deadlock resolution mechanisms</li>
      </ul>

      <hr />

      <h2 id="advisor-angel-agreements">Advisor Angel Agreements</h2>
      <p>
        All advisor angels have invested personally through a standardized{" "}
        <strong>Convertible Capital Loan Agreement</strong> (
        <em>pääomalainaehtoinen vaihtovelkakirjalaina</em>), prepared by
        Hannes Snellman Attorneys. Every advisor is on equal terms — the
        only variable is the loan amount.
      </p>

      <h3>Key terms</h3>
      <table>
        <thead>
          <tr>
            <th>Term</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Instrument</td>
            <td>
              Convertible capital loan under Chapter 12 of the Finnish
              Companies Act
            </td>
          </tr>
          <tr>
            <td>Conversion valuation</td>
            <td>Fixed post-money EUR 5,000,000</td>
          </tr>
          <tr>
            <td>Conversion per EUR 25k</td>
            <td>0.5% ownership on a fully diluted basis</td>
          </tr>
          <tr>
            <td>Ownership range per advisor</td>
            <td>0.5% — 2% (depending on loan amount)</td>
          </tr>
          <tr>
            <td>Maturity</td>
            <td>24 months from signing</td>
          </tr>
          <tr>
            <td>Interest</td>
            <td>
              None for first 12 months; statutory default interest
              thereafter if not converted
            </td>
          </tr>
          <tr>
            <td>Conversion trigger</td>
            <td>
              Automatic upon Qualified Investment Round (EUR 500k+ aggregate
              new investment)
            </td>
          </tr>
          <tr>
            <td>Anti-dilution</td>
            <td>Full ratchet if QIR valuation is below EUR 5M</td>
          </tr>
          <tr>
            <td>Exit conversion</td>
            <td>
              Automatic at EUR 2.5M post-money (2x the shares per EUR
              invested)
            </td>
          </tr>
          <tr>
            <td>Repayment</td>
            <td>Conversion only — neither party can demand cash repayment</td>
          </tr>
          <tr>
            <td>Ranking</td>
            <td>Subordinated capital loan, pari passu with other CLAs</td>
          </tr>
          <tr>
            <td>MFN clause</td>
            <td>
              If any CLA is amended to more favorable terms, all lenders
              can request the same
            </td>
          </tr>
          <tr>
            <td>Governing law</td>
            <td>Finland</td>
          </tr>
          <tr>
            <td>Disputes</td>
            <td>Arbitration under Finland Chamber of Commerce rules</td>
          </tr>
        </tbody>
      </table>

      <Details variant="info" title="How Conversion Works">
        <p>
          Upon a Qualified Investment Round (new share issue of EUR 500k+),
          all convertible loans automatically convert into the same share
          class issued in that round. Each EUR 25,000 converts to 0.5%
          ownership on a fully diluted basis at a fixed EUR 5M post-money
          valuation — before execution of the investment round.
        </p>
        <p>
          If no QIR occurs within 24 months, the loan converts at the same
          EUR 5M valuation into the most favorable existing share class.
        </p>
        <p>
          In an exit event before conversion, the valuation drops to EUR
          2.5M — effectively doubling the advisor's ownership per EUR
          invested as reward for early-stage risk.
        </p>
      </Details>

      <Details variant="info" title="Investor Protections">
        <ul>
          <li>
            <strong>Full ratchet anti-dilution</strong> if the QIR valuation
            is below EUR 5M
          </li>
          <li>
            <strong>MFN clause</strong> — if any CLA gets better terms, all
            lenders can request the same
          </li>
          <li>
            <strong>Quarterly reporting</strong> — financial and business
            metrics within 30 days of quarter end
          </li>
          <li>
            <strong>Dividend restriction</strong> — no distributions while
            loans are outstanding
          </li>
          <li>
            <strong>Related-party transaction restriction</strong> — no
            out-of-ordinary-course transactions with shareholders
          </li>
          <li>
            <strong>Debt restriction</strong> — no new indebtedness outside
            ordinary business (Finnvera/BF excluded)
          </li>
          <li>
            <strong>Shareholders' agreement</strong> — upon conversion,
            lenders join the SHA on customary terms
          </li>
        </ul>
      </Details>

      <Details
        variant="note"
        title="Full Agreement Text — Convertible Capital Loan Agreement (Hannes Snellman, March 2026)"
      >
        <p>
          <em>
            Confidential Draft | 23 March 2026 — Subject to amendments
          </em>
        </p>

        <p>
          <strong>Convertible Capital Loan Agreement</strong>
        </p>

        <p>
          This Convertible Capital Loan Agreement (the "Agreement") is
          entered into on [date] 2026 by and between:
        </p>

        <p>
          (1) <strong>Taiga AI Oy</strong>, business ID 3607851-2, a limited
          liability company organised and existing under the laws of
          Finland and having its registered office at c/o Lapinlahdenkatu
          16, 00180 Helsinki, Finland (the "Company" or "Borrower"); and
        </p>

        <p>
          (2) <strong>[Name of Investor]</strong>, [Business ID / date of
          birth], [a limited liability company organised and existing
          under the laws of [jurisdiction] and having its registered office
          at] [address] (the "Lender").
        </p>

        <p>
          The Company and the Lender are hereinafter referred to
          collectively as the Parties and each separately as a Party.
        </p>

        <hr />

        <p>
          <strong>Background</strong>
        </p>

        <p>
          (A) The Borrower is a newly established company that needs short
          to medium term financing to start and develop its business
          operations. To provide the Company immediate financing for its
          general corporate purposes, the Lender has agreed to grant to the
          Borrower, and the Borrower has agreed to obtain from the Lender,
          a convertible subordinated loan (
          <em>pääomalainaehtoinen vaihtovelkakirjalaina</em>) of EUR
          [amount] in accordance with the terms and conditions of this
          Agreement (the "Loan").
        </p>

        <p>
          (B) The Company takes out the Loan as part of a broader financing
          arrangement intended to secure the Company's short to medium
          term financing needs. Thus, the Company has a weighty financial
          reason pursuant to Chapter 9, Section 4(1) of the Finnish
          Companies Act for taking out the Loan from the Lender and
          subsequently issuing shares to the Lender pursuant to a possible
          conversion of the Loan.
        </p>

        <p>
          (C) The Parties acknowledge and agree that the Borrower is aiming
          to enter into other convertible capital loan agreements with
          certain other lenders on equal terms and conditions with this
          Agreement (as applicable and save for the Loan amount) (such
          other convertible capital loan agreements together with this
          Agreement, the "CLAs").
        </p>

        <p>
          (D) All Parties have carefully and independently assessed the
          rights and obligations which this Agreement confers on them and
          also the risks and opportunities relating to the arrangements
          contained herein.
        </p>

        <hr />

        <p>
          <strong>1. The Loan Amount and Drawdown of The Loan</strong>
        </p>

        <p>
          1.1 Subject to the terms and conditions set out in this
          Agreement, the Lender agrees, severally and not jointly with any
          other third party, to make available to the Borrower a
          convertible loan in the amount of EUR [amount] (the Loan). The
          Loan shall be made available to the Borrower on the Borrower's
          account within ten (10) business days of the date of this
          Agreement, as indicated by the Borrower to the Lender in
          writing.
        </p>

        <p>
          1.2 No interest shall accrue on the Loan prior to a date that is
          twelve months from the signing of the Agreement (the "Interest
          Date"). If the Loan has not been converted into new shares before
          the Interest Date, the Loan shall start accruing default interest
          in accordance with the Finnish Interest Act (633/1982, as
          amended), calculated as from the Interest Date until the date of
          full repayment of all outstanding principal under the Loan and
          default interest accrued thereon.
        </p>

        <p>
          1.3 The Loan shall be governed by the provisions of Chapter 12 of
          the Finnish Companies Act (624/2006, as amended) concerning
          capital loans (<em>pääomalaina</em>). The Lender acknowledges
          that the Loan shall rank below any other existing or future
          debts of the Company and pari passu with any other possible
          unsecured capital loan creditors of the Company. For the sake of
          clarity, the Parties acknowledge and agree that any repayment by
          the Borrower of the Loan pursuant to this Agreement may only
          take place in compliance with the provisions of Chapter 12 of
          the Finnish Companies Act and, when allowed pursuant to the said
          act, this Agreement.
        </p>

        <hr />

        <p>
          <strong>2. Loan Period and Repayment</strong>
        </p>

        <p>
          2.1 The Loan falls due for repayment through conversion in one
          (1) instalment at the earlier of the following: (i) on the date
          that falls 24 months after the date of signing of this Agreement
          (the "Maturity Date"), or (ii) immediately prior to or in
          connection with closing of the Qualified Investment Round (as
          defined below).
        </p>

        <p>
          2.2 The Loan shall be repaid by converting the Loan into new
          shares in the Borrower as set out in this Agreement, and neither
          Party shall have the right to demand the repayment of the Loan
          in cash.
        </p>

        <hr />

        <p>
          <strong>3. Conversion</strong>
        </p>

        <p>
          3.1 The Loan shall be converted into new shares in the Borrower
          in accordance with the terms set out in this Section 3.
        </p>

        <p>
          <strong>Conversion upon a Qualified Investment Round:</strong>{" "}
          Upon the occurrence of a new share issue or multiple related new
          share issues whereby the Borrower issues shares to one or more
          new or existing investor(s) against a consideration amounting in
          the aggregate to at least EUR 500,000 (five hundred thousand)
          (in addition to the consideration received pursuant to the
          conversion of this Loan and any other CLAs) (the "Qualified
          Investment Round") before the Maturity Date, the Loan shall
          automatically be converted into new shares that are equal to the
          share class issued on the Qualified Investment Round. In the
          conversion, this Loan will automatically convert into new shares
          with a fixed post-money valuation of EUR 5,000,000 (five
          million) (i.e., so that each EUR 25,000 will result in an
          ownership of 0.5% of the shares in the Company on a fully
          diluted basis after the conversion and before execution of the
          Qualified Investment Round).
        </p>

        <p>
          <strong>Conversion upon the Maturity Date of the Loan:</strong>{" "}
          In the event that no Qualified Investment Round has taken place
          before the Maturity Date of the Loan at the latest, then the
          Loan shall automatically be converted into new shares in the
          then existing share class with most favourable terms. In the
          conversion, this Loan will automatically convert into new shares
          with a fixed post-money valuation of EUR 5,000,000 (five
          million) (i.e., so that each EUR 25,000 will result in an
          ownership of 0.5% of the shares in the Company on a fully
          diluted basis after the conversion).
        </p>

        <p>
          <strong>Conversion upon Exit:</strong> In the event that an Exit
          event, constituting a sale of all or substantially all of the
          shares or assets of the Company to a third party, or a merger or
          other type of transaction or series of transactions in which
          ownership and/or control of the Company or all or substantially
          all of its assets are transferred to a third party, occurs
          before a Qualified Investment Round and the Maturity Date, the
          Loan shall automatically be converted into new shares in the
          then existing share class with most favourable terms with a
          fixed post-money valuation of EUR 2,500,000 (two million five
          hundred thousand) immediately prior to the completion of such
          Exit event (i.e., so that each EUR 25,000 will result in an
          ownership of 1.0% of the shares in the Company on a fully
          diluted basis after the conversion and before execution of the
          Exit event).
        </p>

        <p>
          3.2 If the Company executes a Qualified Investment Round with a
          valuation of less than EUR 5,000,000 (five million), the Lender
          is entitled to a full ratchet anti-dilution adjustment. The
          adjustment is to be carried out so that the Company will issue
          such number of new shares free of charge to the Lender as is
          necessary to ensure that the Lender holds the same number of
          shares as it would have held had it originally agreed to convert
          the shares at such lower valuation.
        </p>

        <p>
          3.3 In any event of conversion, the payment of the shares to be
          issued to the Lender as result of the conversion of the Loan
          shall take effect by using the Lender's receivable based on this
          Agreement to be set off against the subscription price of the
          shares, and the payment of the subscription price of the shares
          shall be made into the reserve for invested unrestricted equity
          (<em>sijoitetun vapaan oman pääoman rahasto</em>) of the
          Borrower. To the extent a Loan cannot be converted in its
          entirety due to the reason that partial shares cannot be issued,
          the remaining amount, if any, shall be deemed cancelled.
        </p>

        <p>
          3.4 In any event of conversion, each Party and each of the
          shareholders in the Borrower undertake to execute, make and do
          all such resolutions, documents and acts as may be necessary for
          effectively completing the conversion of the Loan into new
          shares and registration of such new shares in accordance with
          this Section 3. The Board of Directors of the Borrower shall
          resolve on all procedural matters applicable to the share
          subscription and on the conversion. Notwithstanding anything to
          the contrary herein, a prerequisite for any subscription of
          shares in the Borrower based on this Agreement is that the
          Lender simultaneously adheres as a party to the main
          shareholders' agreement of the Company or another shareholders'
          agreement of the Borrower generally applicable to similarly
          situated shareholders in the form approved by the Board of
          Directors of the Borrower, in each case on terms that can be
          considered customary for similar type of transactions. It is
          acknowledged and agreed that such shareholders' agreement shall
          not include terms that are unreasonable from the Lender's
          perspective or that impose new personal liabilities on the
          Lender. If the Lender would not accept to enter into such
          shareholders' agreement, the Lender shall be entitled to
          negotiate with the Company on a transfer, sale or redemption of
          the Loan or converted shares, as applicable.
        </p>

        <hr />

        <p>
          <strong>4. Payments</strong>
        </p>

        <p>
          4.1 Should the repayment date of the Loan fall on a day that is
          not a business day, then the repayment date of the Loan shall
          be the next following business day on which banks are open for
          normal business in Helsinki, Finland.
        </p>

        <p>
          4.2 The Parties acknowledge and agree that any loan repayments
          under the CLAs shall be made pro rata to the lenders' respective
          outstanding loan amounts under the CLAs.
        </p>

        <hr />

        <p>
          <strong>5. Taxes</strong>
        </p>

        <p>
          5.1 For the avoidance of doubt, the Borrower shall under no
          circumstances be obligated to compensate the Lender for any
          taxes, levies, duties or corresponding deductions or charges
          payable by the Lender on any income generated based on this
          Agreement.
        </p>

        <hr />

        <p>
          <strong>6. Shareholder Rights</strong>
        </p>

        <p>
          6.1 The shares issued based on the conversion made under this
          Agreement entitle to dividend with respect to the financial year
          in which the issuance takes place. Other shareholder rights will
          commence on the date on which the shares subscribed for by the
          Lender in accordance with this Agreement are registered with
          the Finnish Trade Register.
        </p>

        <hr />

        <p>
          <strong>7. Representations</strong>
        </p>

        <p>
          7.1 The Lender represents and warrants to the Company as
          follows:
        </p>

        <ul>
          <li>
            (i) the Lender has full legal right, power, capacity and
            authority to execute this Agreement and to perform the
            Lender's obligations hereunder;
          </li>
          <li>
            (ii) this Agreement constitutes a valid and legally binding
            agreement of the Lender and no other action is necessary to
            authorize the execution and delivery of this Agreement; and
          </li>
          <li>
            (iii) the Lender's entry into this Agreement is in compliance
            with all applicable laws and regulations, including related to
            any applicable sanctions or money-laundering regulations.
          </li>
        </ul>

        <p>
          7.2 The Company represents and warrants to the Lender as
          follows:
        </p>

        <ul>
          <li>
            (i) the Company is a newly established company with no
            previous business operations, liabilities or obligations
            outside the ordinary course of business;
          </li>
          <li>
            (ii) the Company does not infringe or misuse any third party
            intellectual property rights, and there are no circumstances
            likely to give rise to any such claim or allegation of
            infringement or unauthorised use; and
          </li>
          <li>
            (iii) there are no outstanding claims, disputes, or
            proceedings relating to the Company.
          </li>
        </ul>

        <hr />

        <p>
          <strong>8. Undertakings</strong>
        </p>

        <p>8.1 The Company shall provide to the Lender:</p>

        <ul>
          <li>
            (i) within 30 days after the end of every calendar quarter, a
            financial and business metrics report;
          </li>
          <li>
            (ii) unaudited annual accounts within 60 days from the end of
            each financial year; and
          </li>
          <li>
            (iii) audited annual accounts as soon as possible after such
            accounts have been prepared and audited, but not later than
            six months from the end of each financial year.
          </li>
        </ul>

        <p>
          8.2 The Company undertakes to the Lender that it shall not,
          during the period that a Loan is outstanding:
        </p>

        <ul>
          <li>
            (i) distribute dividends or other funds to the shareholders;
          </li>
          <li>
            (ii) enter into any agreement, assignment or other transaction
            with a shareholder or its immediate family member or any
            entity controlled by a shareholder and/or its immediate family
            member(s) out of the ordinary course of business; or
          </li>
          <li>
            (iii) incur indebtedness, other than in order to develop its
            operations in the ordinary course of business, including in
            financing or guarantee arrangements with Finnvera and Business
            Finland.
          </li>
        </ul>

        <hr />

        <p>
          <strong>9. Miscellaneous</strong>
        </p>

        <p>
          9.1 <strong>Notices.</strong> All notices and other communication
          required under this Agreement shall be delivered to Lender's
          e-mail address as notified to the Company by the Lender.
        </p>

        <p>
          9.2 <strong>Entire Agreement.</strong> This Agreement constitutes
          the entire understanding and agreement between the Parties with
          respect to the subject matter covered and supersedes all prior
          negotiations, understandings, and agreements, whether written or
          oral, relating to the subject matter covered.
        </p>

        <p>
          9.3 <strong>Amendments.</strong> Any amendment to this Agreement
          shall be in writing and shall have no effect before signed by
          duly authorised representatives of each Party. Should any CLA
          be amended after the date hereof to grant to the relevant lender
          more favourable terms, the Company shall notify such lender and
          the relevant lenders under other CLAs shall have the right to
          require amendment of their respective loan agreement mutatis
          mutandis.
        </p>

        <p>
          9.4 <strong>Assignability.</strong> No Party is entitled to
          transfer, assign or otherwise dispose of this Agreement or its
          rights and obligations under this Agreement to any third party
          without the prior written consent of the other Party.
        </p>

        <p>
          9.5 <strong>No Waiver.</strong> Any failure or delay by a Party
          in exercising any right or remedy will not constitute a waiver.
        </p>

        <p>
          9.6 <strong>Confidentiality.</strong> The Lender agrees to keep
          confidential and not to use or disclose to any third party any
          information received or obtained from the Company in connection
          with this Agreement, including intellectual property rights,
          trade secrets, and confidential business information, unless
          required by law, consented to by the Board, or disclosed to
          professional advisors under confidentiality obligations.
        </p>

        <p>
          9.7 <strong>Severability.</strong> If any part of this Agreement
          is determined to be invalid or unenforceable, such determination
          shall not invalidate any other provision of this Agreement.
        </p>

        <hr />

        <p>
          <strong>10. Other Provisions</strong>
        </p>

        <p>
          10.1 No Party shall be responsible for delays and damages caused
          by an impediment beyond its control (force majeure).
        </p>

        <p>
          10.2 This Agreement does not contain any other obligations for
          the Lender to finance the operations of the Borrower.
        </p>

        <hr />

        <p>
          <strong>11. Governing Law.</strong> This Agreement is governed
          by the laws of Finland without regard to its principles and
          rules on conflict of laws.
        </p>

        <p>
          <strong>12. Settlement of Disputes.</strong> Any dispute arising
          out of or in connection with this Agreement shall be finally
          settled by arbitration in accordance with the Arbitration Rules
          of the Finland Chamber of Commerce. The seat of the arbitration
          shall be Helsinki, Finland. The language shall be English,
          unless the Parties agree otherwise. Evidence may be submitted
          and witnesses heard in Finnish.
        </p>

        <p>
          <strong>13. Counterparts and Electronic Signatures.</strong>{" "}
          This Agreement may be executed in any number of counterparts and
          signed electronically.
        </p>

        <hr />

        <p>
          <em>Prepared by Hannes Snellman Attorneys Ltd.</em>
        </p>
      </Details>

      <hr />

      <h2>Cloud2 Departure</h2>
      <p>
        All four founders departed Cloud2 on good terms — friendly exit,
        good leaver status. Founders remain Cloud2 shareholders and
        informal advisors with no active obligations. A limited non-compete
        applies to Cloud2's consulting services business only. There are no
        restrictions on TAIGA's business, including targeting Cloud2's
        customer base for TAIGA's platform. No earn-out obligations.
        Non-solicitation of Cloud2 employees applies.
      </p>
      <p>
        TAIGA and Cloud2 operate in different markets — Cloud2 is a cloud
        consulting services company, TAIGA is an AI governance platform
        company. There is no business overlap.
      </p>

      <hr />

      <h2>Intellectual Property</h2>
      <p>
        All pre-incorporation IP created by founders for TAIGA has been
        assigned to the company at incorporation. The platform is built on
        standard open-source licenses (MIT, Apache 2.0). No GPL
        dependencies. No proprietary third-party IP has been incorporated.
      </p>

      <p>The core proprietary IP includes:</p>
      <ul>
        <li>
          <strong>LFA governance framework</strong> — the two-matrix
          Logical Framework for Autonomous Action
        </li>
        <li>
          <strong>Policy engine</strong> — hierarchical policy evaluation
          system (Company -&gt; Team -&gt; Project)
        </li>
        <li>
          <strong>Discovery pipeline</strong> — 8-artifact dependency graph
          with automated generation
        </li>
        <li>
          <strong>LLM Council pattern</strong> — multi-model cross-review
          for high-stakes generation
        </li>
        <li>
          <strong>Coding agent eval harness</strong> — 5-tier evaluation
          framework
        </li>
      </ul>

      <hr />

      <h2>Regulatory Position</h2>
      <table>
        <thead>
          <tr>
            <th>Area</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>GDPR</td>
            <td>
              Compliant by design. DPIA completed. Data processing
              documented.
            </td>
          </tr>
          <tr>
            <td>EU AI Act</td>
            <td>
              Architecture designed for compliance. Audit trails,
              transparency, human oversight built in.
            </td>
          </tr>
          <tr>
            <td>Finnish Companies Act</td>
            <td>
              Convertible loans structured under Chapter 12 (capital
              loans).
            </td>
          </tr>
          <tr>
            <td>Data residency</td>
            <td>EU-only, infrastructure-enforced via AWS SCPs.</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>Legal Counsel</h2>
      <p>
        Corporate and investment documentation prepared by{" "}
        <strong>Hannes Snellman Attorneys Ltd</strong>, one of the leading
        Nordic law firms specializing in technology companies, venture
        capital, and cross-border transactions.
      </p>
    </>
  );
}
